import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCanvasStore } from '@/stores/canvasStore';
import { SpatialCluster } from '@/lib/spatialClustering';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { FolderPlus, Pencil } from 'lucide-react';
import { toast } from 'sonner';

interface ClusterOverlayProps {
  zoom: number;
  panX: number;
  panY: number;
}

const CLUSTER_PADDING = 24;
const BORDER_RADIUS = 16;
const MIN_Y = 160; // Minimum Y for drag constraint - must match clusterLayout.ts

const CLUSTER_PALETTE = [
  { fill: 'rgba(139, 115, 207, 0.10)', border: 'rgba(139, 115, 207, 0.30)', text: 'rgb(107, 83, 175)' },
  { fill: 'rgba(45, 158, 128, 0.10)', border: 'rgba(45, 158, 128, 0.30)', text: 'rgb(30, 130, 105)' },
  { fill: 'rgba(210, 125, 65, 0.10)', border: 'rgba(210, 125, 65, 0.30)', text: 'rgb(180, 95, 35)' },
  { fill: 'rgba(65, 135, 205, 0.10)', border: 'rgba(65, 135, 205, 0.30)', text: 'rgb(40, 110, 180)' },
  { fill: 'rgba(190, 80, 105, 0.10)', border: 'rgba(190, 80, 105, 0.30)', text: 'rgb(165, 55, 80)' },
  { fill: 'rgba(110, 170, 70, 0.10)', border: 'rgba(110, 170, 70, 0.30)', text: 'rgb(85, 145, 45)' },
  { fill: 'rgba(180, 140, 60, 0.10)', border: 'rgba(180, 140, 60, 0.30)', text: 'rgb(155, 115, 35)' },
  { fill: 'rgba(100, 100, 180, 0.10)', border: 'rgba(100, 100, 180, 0.30)', text: 'rgb(75, 75, 155)' },
];

interface ClusterBoundaryProps {
  cluster: SpatialCluster;
  colorIndex: number;
  onHover: (clusterId: string | null) => void;
  onSelect: (cluster: SpatialCluster) => void;
  isHovered: boolean;
  onDragStart?: (e: React.MouseEvent) => void;
  onConvertToBoard: (cluster: SpatialCluster) => void;
  onRename: (cluster: SpatialCluster, newName: string) => void;
  isEditing: boolean;
  onStartEditing: () => void;
  onStopEditing: () => void;
}

function ClusterBoundary({ 
  cluster, 
  colorIndex, 
  onHover,
  onSelect,
  isHovered,
  onDragStart,
  onConvertToBoard,
  onRename,
  isEditing,
  onStartEditing,
  onStopEditing,
}: ClusterBoundaryProps) {
  const { items } = useCanvasStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [editValue, setEditValue] = useState(cluster.name || 'Cluster');

  const bbox = useMemo(() => {
    const members = items.filter(i => cluster.memberItemIds.includes(i.id));
    if (members.length === 0) return cluster.boundingBox;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const item of members) {
      minX = Math.min(minX, item.position_x);
      minY = Math.min(minY, item.position_y);
      maxX = Math.max(maxX, item.position_x + item.width);
      maxY = Math.max(maxY, item.position_y + item.height);
    }
    return {
      minX, minY, maxX, maxY,
      width: maxX - minX,
      height: maxY - minY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
    };
  }, [items, cluster.memberItemIds, cluster.boundingBox]);

  const isUnclustered = (cluster.name || '').toLowerCase() === 'unclustered';
  const palette = CLUSTER_PALETTE[colorIndex % CLUSTER_PALETTE.length];
  const bgColor = isUnclustered ? 'rgba(160, 160, 160, 0.06)' : palette.fill;
  const borderColor = isUnclustered ? 'rgba(160, 160, 160, 0.25)' : palette.border;
  const textColor = isUnclustered ? 'rgba(120, 120, 120, 0.8)' : palette.text;

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditValue(cluster.name || 'Cluster');
    onStartEditing();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editValue.trim()) {
        onRename(cluster, editValue.trim());
      }
      onStopEditing();
    } else if (e.key === 'Escape') {
      setEditValue(cluster.name || 'Cluster');
      onStopEditing();
    }
  };

  const handleBlur = () => {
    if (editValue.trim() && editValue !== cluster.name) {
      onRename(cluster, editValue.trim());
    }
    onStopEditing();
  };

  return (
    <g>
      <rect
        x={bbox.minX - CLUSTER_PADDING}
        y={bbox.minY - CLUSTER_PADDING}
        width={bbox.width + CLUSTER_PADDING * 2}
        height={bbox.height + CLUSTER_PADDING * 2}
        rx={BORDER_RADIUS}
        ry={BORDER_RADIUS}
        fill={bgColor}
        stroke={borderColor}
        strokeWidth={isHovered ? 2.5 : 1.5}
        strokeDasharray={isUnclustered ? '8 4' : undefined}
        className="pointer-events-none"
      />

      <foreignObject
        x={bbox.minX - CLUSTER_PADDING}
        y={bbox.minY - CLUSTER_PADDING - 36}
        width={bbox.width + CLUSTER_PADDING * 2}
        height={32}
        className="pointer-events-auto overflow-visible"
      >
        {isEditing ? (
          <div className="flex items-center justify-center h-full">
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onMouseDown={(e) => e.stopPropagation()}
              className="px-3 py-1 text-xs font-medium rounded-full border shadow-sm bg-background focus:outline-none focus:ring-2 focus:ring-partnr/50"
              style={{
                borderColor: borderColor,
                color: textColor,
                minWidth: '100px',
                maxWidth: '200px',
              }}
            />
          </div>
        ) : (
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div
                className="flex items-center justify-center h-full cursor-grab"
                onMouseEnter={() => onHover(cluster.id)}
                onMouseLeave={() => onHover(null)}
                onMouseDown={onDragStart}
                onDoubleClick={handleDoubleClick}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(cluster);
                }}
              >
                <div
                  className={`px-3 py-1.5 rounded-full shadow-sm text-xs font-medium flex items-center gap-1.5 border hover:shadow-md transition-shadow ${isUnclustered ? 'opacity-60' : ''}`}
                  style={{
                    backgroundColor: 'hsl(var(--background) / 0.95)',
                    borderColor: borderColor,
                    color: textColor,
                    borderStyle: isUnclustered ? 'dashed' : 'solid',
                  }}
                >
                  <span>{cluster.name || 'Cluster'}</span>
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span style={{ opacity: 0.7 }}>{cluster.memberItemIds.length}</span>
                </div>
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent className="w-56">
              <ContextMenuItem 
                onClick={() => {
                  setEditValue(cluster.name || 'Cluster');
                  onStartEditing();
                  setTimeout(() => inputRef.current?.focus(), 50);
                }}
                className="cursor-pointer"
              >
                <Pencil className="mr-2 h-4 w-4" />
                <span>Rename Cluster</span>
              </ContextMenuItem>
              <ContextMenuItem 
                onClick={() => onConvertToBoard(cluster)}
                className="cursor-pointer"
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                <span>Convert to Board</span>
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        )}
      </foreignObject>
    </g>
  );
}

interface DraggableClusterBoundaryProps extends Omit<ClusterBoundaryProps, 'onDragStart'> {}

function DraggableClusterBoundary({
  cluster,
  colorIndex,
  onHover,
  onSelect,
  isHovered,
  onConvertToBoard,
  onRename,
  isEditing,
  onStartEditing,
  onStopEditing,
}: DraggableClusterBoundaryProps) {
  const dragStartRef = useRef<{ startX: number; startY: number; startPositions: Map<string, { x: number; y: number }> } | null>(null);
  const { items, updateItem } = useCanvasStore();

  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start drag if editing
    if (isEditing) return;
    
    e.stopPropagation();
    e.preventDefault();

    const startPositions = new Map<string, { x: number; y: number }>();
    const memberItems = items.filter(i => cluster.memberItemIds.includes(i.id));
    for (const item of memberItems) {
      startPositions.set(item.id, { x: item.position_x, y: item.position_y });
    }

    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPositions,
    };

    const handleMouseMove = (moveE: MouseEvent) => {
      if (!dragStartRef.current) return;
      const { zoom } = useCanvasStore.getState();
      const dx = (moveE.clientX - dragStartRef.current.startX) / zoom;
      const dy = (moveE.clientY - dragStartRef.current.startY) / zoom;

      for (const [itemId, startPos] of dragStartRef.current.startPositions) {
        updateItem(itemId, {
          position_x: Math.max(20, Math.round((startPos.x + dx) / 20) * 20),
          position_y: Math.max(MIN_Y, Math.round((startPos.y + dy) / 20) * 20),
        });
      }
    };

    const handleMouseUp = () => {
      dragStartRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <ClusterBoundary
      cluster={cluster}
      colorIndex={colorIndex}
      onHover={onHover}
      onSelect={onSelect}
      isHovered={isHovered}
      onDragStart={handleMouseDown}
      onConvertToBoard={onConvertToBoard}
      onRename={onRename}
      isEditing={isEditing}
      onStartEditing={onStartEditing}
      onStopEditing={onStopEditing}
    />
  );
}

export function ClusterOverlay({ zoom, panX, panY }: ClusterOverlayProps) {
  const { 
    spatialClusters, 
    isClusteringEnabled, 
    highlightedClusterId, 
    setSelectedItems,
    createBoard,
    moveItemsToBoard,
    currentBoardId,
    setSpatialClusters,
    items,
  } = useCanvasStore();
  const [hoveredClusterId, setHoveredClusterId] = useState<string | null>(null);
  const [editingClusterId, setEditingClusterId] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleClusterSelect = useCallback((cluster: SpatialCluster) => {
    setSelectedItems(new Set(cluster.memberItemIds));
  }, [setSelectedItems]);

  const handleConvertToBoard = useCallback(async (cluster: SpatialCluster) => {
    if (!currentBoardId) {
      toast.error('No current board');
      return;
    }

    const boardName = cluster.name || 'New Board';
    
    // Calculate cluster center position for the board_link card
    const memberItems = items.filter(i => cluster.memberItemIds.includes(i.id));
    
    let centerX = 200;
    let centerY = 200;
    
    if (memberItems.length > 0) {
      const minX = Math.min(...memberItems.map(i => i.position_x));
      const maxX = Math.max(...memberItems.map(i => i.position_x + i.width));
      const minY = Math.min(...memberItems.map(i => i.position_y));
      const maxY = Math.max(...memberItems.map(i => i.position_y + i.height));
      
      // Center the board_link card (assume ~200x150 card size)
      centerX = Math.round((minX + maxX) / 2) - 100;
      centerY = Math.round((minY + maxY) / 2) - 75;
    }
    
    // Create board WITH position (creates board_link card automatically)
    const newBoard = await createBoard(boardName, currentBoardId, { 
      x: centerX, 
      y: centerY 
    });
    if (!newBoard) {
      toast.error('Failed to create board');
      return;
    }

    // Move all cluster items to the new board
    await moveItemsToBoard(cluster.memberItemIds, newBoard.id);

    // Remove this cluster from the list since its items are gone
    setSpatialClusters(spatialClusters.filter(c => c.id !== cluster.id));

    // Show success with option to navigate to the new board
    toast.success(`Created "${boardName}" with ${cluster.memberItemIds.length} items`, {
      action: {
        label: 'Open',
        onClick: () => navigate(`/canvas/${newBoard.id}`),
      },
    });
  }, [currentBoardId, createBoard, moveItemsToBoard, setSpatialClusters, spatialClusters, items, navigate]);

  const handleRename = useCallback((cluster: SpatialCluster, newName: string) => {
    // Update the cluster name in local state
    const updatedClusters = spatialClusters.map(c => 
      c.id === cluster.id ? { ...c, name: newName } : c
    );
    setSpatialClusters(updatedClusters);
    toast.success(`Renamed cluster to "${newName}"`);
  }, [spatialClusters, setSpatialClusters]);

  if (!isClusteringEnabled || spatialClusters.length === 0) return null;

  return (
    <svg 
      className="absolute inset-0 w-full h-full pointer-events-none" 
      style={{ 
        zIndex: 0,
        overflow: 'visible',
      }}
    >
      {spatialClusters.map((cluster, index) => {
        const isHighlighted = hoveredClusterId === cluster.id || highlightedClusterId === cluster.id;
        return (
          <DraggableClusterBoundary
            key={cluster.id}
            cluster={cluster}
            colorIndex={index}
            onHover={setHoveredClusterId}
            onSelect={handleClusterSelect}
            isHovered={isHighlighted}
            onConvertToBoard={handleConvertToBoard}
            onRename={handleRename}
            isEditing={editingClusterId === cluster.id}
            onStartEditing={() => setEditingClusterId(cluster.id)}
            onStopEditing={() => setEditingClusterId(null)}
          />
        );
      })}
    </svg>
  );
}
